import { UserBadge } from '@/models/profile.ts';
import { StoreContent } from '@/store';
import { PoiState } from '@/models/poi';
import { Community } from '@/constants';

const POIS_UNLOCKED_BADGES = [5, 10, 20, 50, 100, 130];
const ROUTES_COMPLETED_BADGES = [1, 3, 5, 10];
const TOPIC_BADGES = [1, 2, 3, 4, 5, 6, 7];
const COMMUNITY_COUNT = 11;

export default class ProgressCalculator {
    public badges: UserBadge[];

    private store: StoreContent;

    public constructor(store: StoreContent) {
        this.store = store;
        this.badges = new Array<UserBadge>();
    }

    get unlockedPoiCount(): number {
        if (!this.store) return 0;
        return this.store.pois.filter(p => p.state === PoiState.UNLOCKED).length;
    }

    get completedRouteCount(): number {
        if (!this.store) return 0;

        let numberOfCompletedRoutes = 0;
        for (const route of this.store.routes) {
            const routePois = this.store.pois.filter(p => p.route === route.id);
            const noLockedPois = routePois.every(p => p.state === PoiState.UNLOCKED);
            if (noLockedPois) numberOfCompletedRoutes++;
        }

        return numberOfCompletedRoutes;
    }

    get achievedBadgesCount(): number {
        if (!this.badges) return 0;
        return this.badges.filter(b => b.achieved === true).length;
    }

    get communityCount(): number {
        return COMMUNITY_COUNT;
    }

    get completedCommunityCount(): number {
        if (!this.store) return 0;

        let numberOfCompletedCommunities = 0;
        Object.values(Community).forEach(community => {
            const poisInCommunity = this.poisInCommunityCount(community);
            const unlockedInCommunity = this.unlockedPoisInCommunityCount(community);
            if (unlockedInCommunity >= poisInCommunity) numberOfCompletedCommunities++;
        });

        return numberOfCompletedCommunities;
    }

    public poisInCommunityCount(community: string): number {
        if (!this.store) return 0;
        return this.store.pois.filter(poi => poi.community === community).length;
    }

    public poisWithTopicCount(topic: number): number {
        if (!this.store) return 0;
        return this.store.pois.filter(poi => poi.topics.includes(topic)).length;
    }

    public unlockedPoisInCommunityCount(community: string): number {
        if (!this.store) return 0;
        return this.store.pois.filter(poi => poi.community === community && poi.state === PoiState.UNLOCKED).length;
    }

    public unlockedPoisWithTopicCount(topic: number): number {
        if (!this.store) return 0;
        return this.store.pois.filter(poi => poi.topics.includes(topic) && poi.state === PoiState.UNLOCKED).length;
    }

    public updateBadges(): UserBadge[] {
        const previousBadges = this.badges;

        this.badges = new Array<UserBadge>();
        this.badges.push(...this.calculateNumberOfUnlockedPoiBadges());
        this.badges.push(...this.calculateRoutesCompletedBadges());
        this.badges.push(...this.calculateUnlockedCommunityBadges());
        this.badges.push(...this.calculateUnlockedSpecialPoiBadges());
        this.badges.push(...this.calculateUnlockedTopicBadges());

        return this.getUnlockedBadges(previousBadges);
    }

    public getUnlockedBadges(previousBadges: UserBadge[]) {
        const unlockedBadges = new Array<UserBadge>();
        if (previousBadges && previousBadges.length === this.badges.length) {
            for (let i = 0; i < this.badges.length; i++) {
                const before = previousBadges[i];
                const now = this.badges[i];
                if (before.achieved === false && now.achieved === true) {
                    unlockedBadges.push(now);
                }
            }
        }

        return unlockedBadges;
    }

    public calculateNumberOfUnlockedPoiBadges(): UserBadge[] {
        const badges = new Array<UserBadge>();
        const unlockCount = this.unlockedPoiCount;

        POIS_UNLOCKED_BADGES.forEach((requiredCount, index) => {
            const badge = new UserBadge();
            badge.title = `${requiredCount}`;
            badge.progress = `${Math.min(unlockCount, requiredCount)} / ${requiredCount}`;
            badge.description = this.store.$t('badge.pois_' + (index + 1));
            badge.imgCoin = 'img/badges/coin_poi.svg';
            badge.achieved = unlockCount >= requiredCount;
            badges.push(badge);
        });

        return badges;
    }

    public calculateRoutesCompletedBadges(): UserBadge[] {
        const badges = new Array<UserBadge>();
        const completedCount = this.completedRouteCount;

        ROUTES_COMPLETED_BADGES.forEach((requiredCount, index) => {
            const badge = new UserBadge();
            badge.title = `${requiredCount}`;
            badge.progress = `${Math.min(completedCount, requiredCount)} / ${requiredCount}`;
            badge.description = this.store.$t('badge.routes_' + (index + 1));
            badge.imgCoin = 'img/badges/coin_route.svg';
            badge.achieved = completedCount >= requiredCount;
            badges.push(badge);
        });

        return badges;
    }

    public calculateUnlockedCommunityBadges(): UserBadge[] {
        const badges = new Array<UserBadge>();

        Object.values(Community).forEach((community, index) => {
            const poisInCommunity = this.poisInCommunityCount(community);
            const unlockedInCommunity = this.unlockedPoisInCommunityCount(community);

            const badge = new UserBadge();
            badge.title = this.store.$t('poi_community.' + community) as string;
            badge.progress = `${unlockedInCommunity} / ${poisInCommunity}`;
            badge.description = this.store.$t('badge.communities_' + (index + 1));
            badge.imgCoin = 'img/badges/coin_empty.svg';
            badge.imgBanner = `img/community/${community.toLowerCase()}.png`;
            badge.achieved = unlockedInCommunity >= poisInCommunity;
            badges.push(badge);
        });

        return badges;
    }

    public calculateUnlockedSpecialPoiBadges(): UserBadge[] {
        const badges = new Array<UserBadge>();
        const special_pois = this.store.pois.filter(poi => poi.is_special);

        special_pois.forEach((poi, index) => {
            const badge = new UserBadge();
            badge.title = `${index + 1}`;
            badge.progress = poi.title;
            badge.description = this.store.$t('badge.special_' + (index + 1));
            badge.imgCoin = 'img/badges/coin_ar.svg';
            if (poi.content_ar_experiences && poi.content_ar_experiences.length > 0) {
                const ar = poi.content_ar_experiences[0];
                badge.achieved = this.store.startedScenes.includes(ar.experience);
            }
            badges.push(badge);
        });

        return badges;
    }

    public calculateUnlockedTopicBadges(): UserBadge[] {
        const badges = new Array<UserBadge>();

        TOPIC_BADGES.forEach((topic, index) => {
            const poisWithTopic = this.poisWithTopicCount(topic);
            const unlockedWithTopic = this.unlockedPoisWithTopicCount(topic);
            const topicName = this.store.$t('poi_topics.' + (index + 1)) as string;

            const badge = new UserBadge();
            badge.title = topicName.substr(0, 3).toUpperCase();
            badge.progress = `${unlockedWithTopic} / ${poisWithTopic}`;
            badge.description = this.store.$t('badge.topics_' + (index + 1));
            badge.imgCoin = 'img/badges/coin_topic.svg';
            badge.achieved = unlockedWithTopic >= poisWithTopic;
            badges.push(badge);
        });

        return badges;
    }
}
